"use client";

import { useEffect } from "react";

import { useStudio } from "@/contexts/studio-context";

/**
 * Où la tête de lecture vient d'être posée.
 *
 * Le parcours harmonique en a besoin parce que son écran **suit** le roll : on
 * clique une mesure dans la minimap ou une section dans la structure, et
 * l'accord montré doit être celui qui sonne à cet endroit. Sans ça l'écran
 * tiendrait un compteur à lui, le roll un autre, et les deux se
 * contrediraient dès le premier clic ailleurs.
 *
 * ## Par événement, et pas par image
 *
 * Le rappel se déclenche à l'abonnement, puis à chaque saut du transport. Pas
 * de `requestAnimationFrame` : le produit a retiré les boucles autonomes pour
 * que le studio cesse de chauffer à l'arrêt, et une position d'unité ou
 * d'accord n'a pas besoin d'être relue soixante fois par seconde. Ce qui bouge
 * pendant la lecture est suivi ailleurs, par la boucle qui tourne déjà
 * (`use-harmony-cursor`), et elle ne tourne que quand ça joue.
 *
 * Le premier appel, à l'abonnement, n'est pas un saut : c'est la position
 * actuelle. Elle compte autant, parce qu'un écran qui s'ouvre doit se poser là
 * où le morceau en est, pas au début d'un morceau que le joueur n'écoute plus
 * depuis longtemps.
 */
export function useTransportSeek(onSeek: (tick: number) => void): void {
  const { studio } = useStudio();

  useEffect(() => {
    if (!studio) {
      return;
    }

    const transport = studio.getLocalTransport();

    onSeek(transport.currentTick());

    return transport.on((event) => {
      if (event.type === "seek" || event.type === "loop") {
        onSeek(event.tick);
      }
    });
  }, [studio, onSeek]);
}
